import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from '@/composables/common/router/useRouter';
import { useToast } from '@/composables/common/useToast';
import { config } from '@/config.js';
import indumentariaService from '@/services/admin/bases/indumentarias';
import { withAdminCredentials } from '@/services/admin/adminApiClient';

export function useIndumentariaForm() {
    const route = useRoute();
    const router = useRouter();
    const toast = useToast();

    const listaId = computed(() => route.params.lista_id);
    const indumentariaId = computed(() => route.params.indumentaria_id || null);
    const isEditing = computed(() => !!indumentariaId.value);

    const loading = ref(false);
    const fetching = ref(!!indumentariaId.value);
    const tallasDisponibles = ref([]);

    const formData = ref({
        nombre: '',
        precio: '',
        incluida: false,
        obligatoria: false,
        cantidad_incluida: 1,
        imagen: '',
        imagen_formatted: '',
        tallasSelect: [],
    });

    const localPreview = ref(null);

    const uploadUrl = computed(() =>
        withAdminCredentials(`${config.API_BASE}/upload/image-indumentaria?permisos=public`)
    );

    // ── Fetch existing data (edit mode) ─────────────────────────────────────

    async function fetchIndumentaria() {
        if (!isEditing.value) return;

        fetching.value = true;
        try {
            const data = await indumentariaService.getIndumentaria({
                lista_id: listaId.value,
                indumentaria_id: indumentariaId.value,
            });

            formData.value = {
                nombre: data.nombre || '',
                precio: data.precio || '',
                incluida: !!data.incluida,
                obligatoria: !!data.obligatoria,
                cantidad_incluida: data.cantidad_incluida || 1,
                imagen: data.imagen || '',
                imagen_formatted: data.imagen_formatted || '',
                tallasSelect: Array.isArray(data.tallasSelect) ? data.tallasSelect.map(t => ({
                    ...t,
                    stock_total: t.stock_total ?? 0,
                    stock_seleccionable: t.stock_seleccionable ?? 0,
                })) : [],
            };
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useIndumentariaForm] Error fetching indumentaria:', error);
            }
            toast.error('Error al cargar los datos de la indumentaria');
        } finally {
            fetching.value = false;
        }
    }

    // ── Image handling ──────────────────────────────────────────────────────

    function handleImageUploaded(file, response) {
        formData.value.imagen = response;
        if (localPreview.value?.startsWith('blob:')) {
            URL.revokeObjectURL(localPreview.value);
        }
        localPreview.value = URL.createObjectURL(file);
    }

    function handleImageRemoved() {
        formData.value.imagen = '';
        formData.value.imagen_formatted = '';
        if (localPreview.value?.startsWith('blob:')) {
            URL.revokeObjectURL(localPreview.value);
        }
        localPreview.value = null;
    }

    // ── Tallas ───────────────────────────────────────────────────────────────

    const tallasSeleccionadasIds = computed(() => {
        if (!Array.isArray(formData.value?.tallasSelect)) return [];
        return formData.value.tallasSelect.filter(t => t && t.id).map(t => t.id);
    });

    async function fetchTallas() {
        try {
            const result = await indumentariaService.getTallas({ lista_id: listaId.value });
            tallasDisponibles.value = Array.isArray(result) ? result : [];
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useIndumentariaForm] Error fetching tallas:', error);
            }
        }
    }

    function onTallasChange(selectedIds) {
        const ids = Array.isArray(selectedIds) ? selectedIds : [];
        const existing = Array.isArray(formData.value?.tallasSelect) ? formData.value.tallasSelect : [];
        const existingMap = new Map(existing.map(item => [item.id, item]));

        formData.value.tallasSelect = ids.map(id => {
            const prev = existingMap.get(id);
            const tallaInfo = tallasDisponibles.value.find(t => t.id === id);
            return {
                id,
                nombre: tallaInfo?.nombre || prev?.nombre || '',
                stock_total: prev?.stock_total ?? 0,
                stock_seleccionable: prev?.stock_seleccionable ?? 0,
            };
        });
    }

    // ── Save ────────────────────────────────────────────────────────────────

    async function guardar() {
        loading.value = true;

        const payload = { ...formData.value };

        // Clean up preview-only fields
        delete payload.imagen_formatted;

        try {
            let res;
            if (isEditing.value) {
                res = await indumentariaService.updateIndumentaria({
                    lista_id: listaId.value,
                    indumentaria_id: indumentariaId.value,
                    data: payload,
                });
            } else {
                res = await indumentariaService.createIndumentaria({
                    lista_id: listaId.value,
                    data: payload,
                });
            }

            toast.success(res.data?.message || 'Indumentaria guardada correctamente');
            router.push({
                name: 'back.bases.indumentarias.index',
                params: { lista_id: listaId.value },
            });
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Error al guardar la indumentaria');
        } finally {
            loading.value = false;
        }
    }

    // ── Lifecycle ───────────────────────────────────────────────────────────

    onMounted(() => {
        fetchIndumentaria();
        fetchTallas();
    });

    onUnmounted(() => {
        if (localPreview.value?.startsWith('blob:')) {
            URL.revokeObjectURL(localPreview.value);
        }
    });

    return {
        formData,
        loading,
        fetching,
        isEditing,
        listaId,
        uploadUrl,
        localPreview,
        tallasDisponibles,
        tallasSeleccionadasIds,
        guardar,
        handleImageUploaded,
        handleImageRemoved,
        onTallasChange,
    };
}
